import { useCallback, useEffect, useState } from 'react';
import {
  App,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import { apiErrorMessage } from '@/api/client';
import {
  listMembers,
  inviteMember,
  updateMemberName,
  updateMemberRoles,
  disableMember,
  enableMember,
  type Member,
} from '@/api/members';
import {
  ROLE_COLOR,
  ROLE_LABEL,
  ROLE_OPTIONS,
  normalizeRoles,
  rolesLabel,
  sameRoles,
  type Role,
} from '@/domain/roles';
import { useSessionStore } from '@/store/session';

/**
 * 成员管理。
 *
 * 一个人可以同时具备多个角色，权限取**并集**：带 admin 就能管成员，与是否同时带
 * finance / member 无关。
 *
 * ⚠️ 这里所有的「按钮禁用 / 不显示」都只是**提示**，不是权限。真正的把关全在服务端
 * （backend/src/modules/members/members.service.ts 的四条保护）。前端复刻一遍规则是为了
 * 让人在点下去之前就知道不行，而不是点完看到一个红色 toast。
 */
export default function Members() {
  const { message } = App.useApp();
  const me = useSessionStore((s) => s.me);
  const setMe = useSessionStore((s) => s.setMe);
  // 权限取并集：带 admin 就算管理员，同时带别的角色不影响
  const isAdmin = me?.roles.includes('admin') ?? false;

  const [rows, setRows] = useState<Member[]>([]);
  /**
   * 角色多选框的**未提交**编辑，按 membershipId 存。
   *
   * 🔴 与 rows 分开存是关键：rows 恒为服务端真值，改角色时不就地改它。多选框每勾一下都
   * 提交一次的话，「去掉 admin、加上 finance」这种两步操作里，中间那一步会先撞 LAST_ADMIN；
   * 而就地改 rows 又会让失败后没有真值可回退。
   */
  const [draft, setDraft] = useState<Record<string, Role[]>>({});
  /** 姓名的**未提交**编辑，按 membershipId 存。与 draft 同理：rows 恒为服务端真值。 */
  const [nameDraft, setNameDraft] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<{ email: string; name: string; roles: Role[] }>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listMembers());
      // 服务端真值回来了，丢掉所有未提交的编辑
      setDraft({});
      setNameDraft({});
    } catch (err) {
      message.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    // 取数是 effect 的正当用途（与外部系统同步），这里的 setState 不是级联渲染问题
    // oxlint-disable-next-line react/set-state-in-effect
    void load();
  }, [load]);

  /** 组织里还有几个 active 管理员 —— 决定「最后一个管理员」的控件要不要禁用。 */
  const activeAdmins = rows.filter((r) => r.roles.includes('admin') && r.status === 'active').length;

  /** 这一行是不是「最后一个管理员」——摘 admin 和移除都要挡。 */
  function isLastAdmin(row: Member): boolean {
    return row.roles.includes('admin') && row.status === 'active' && activeAdmins <= 1;
  }

  async function submitInvite() {
    let values: { email: string; name: string; roles: Role[] };
    try {
      values = await form.validateFields();
    } catch {
      return; // 字段上已有报错文案
    }
    setSubmitting(true);
    try {
      // 后端的 email format 校验跑在 trim 之前，带空格会直接 400，所以在边界处 trim
      await inviteMember({
        email: values.email.trim(),
        name: values.name.trim(),
        roles: normalizeRoles(values.roles),
      });
      message.success('已加入，并已发送邀请邮件');
      setInviting(false);
      form.resetFields();
      void load();
    } catch (err) {
      message.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * 提交姓名变更。**只有管理员能走到这里** —— 姓名是组织内的显示名，成员自己改不了
   * （服务端 /members/name 是 requireAdmin，前端这里只是不显示编辑入口）。
   */
  async function commitName(row: Member) {
    const next = nameDraft[row.membershipId];
    if (next === undefined) return; // 没动过

    const dropDraft = () =>
      setNameDraft(({ [row.membershipId]: _drop, ...rest }) => rest);

    const name = next.trim();
    if (!name) {
      message.warning('姓名不能为空');
      dropDraft(); // 退回服务端真值，输入框里的空值不留着
      return;
    }
    if (name === row.name) {
      dropDraft();
      return;
    }

    try {
      await updateMemberName(row.membershipId, name);
      message.success(`已将姓名改为 ${name}`);
      // 改的是自己：顶栏和概览页都从 session 里读姓名，就地更新一下，免得还显示旧名字
      if (me && row.userId === me.user.id) {
        setMe({ ...me, user: { ...me.user, name } });
      }
    } catch (err) {
      message.error(apiErrorMessage(err));
    } finally {
      void load(); // 成功或失败都以服务端为准重新拉一次
    }
  }

  /**
   * 提交角色变更（下拉收起时触发）。语义是**整体覆盖**：多选框里剩下的就是这个人之后的
   * 全部角色，不是增量增删。
   */
  async function commitRoles(row: Member) {
    const next = draft[row.membershipId];
    if (!next) return; // 没动过

    if (sameRoles(row.roles, next)) {
      setDraft(({ [row.membershipId]: _drop, ...rest }) => rest);
      return;
    }
    if (next.length === 0) {
      message.warning('每个成员至少要有一个角色');
      setDraft(({ [row.membershipId]: _drop, ...rest }) => rest);
      return;
    }
    try {
      await updateMemberRoles(row.membershipId, normalizeRoles(next));
      message.success(`已将 ${row.name} 设为${rolesLabel(next)}`);
    } catch (err) {
      message.error(apiErrorMessage(err));
    } finally {
      void load(); // 成功或失败都以服务端为准重新拉一次
    }
  }

  async function toggleStatus(row: Member) {
    try {
      if (row.status === 'active') {
        await disableMember(row.membershipId);
        message.success(`已移除 ${row.name}`);
      } else {
        await enableMember(row.membershipId);
        message.success(`已恢复 ${row.name}`);
      }
      void load();
    } catch (err) {
      message.error(apiErrorMessage(err));
    }
  }

  return (
    <Card
      title="成员"
      extra={
        isAdmin ? (
          <Button type="primary" onClick={() => setInviting(true)}>
            添加成员
          </Button>
        ) : null
      }
    >
      <Table<Member>
        rowKey="membershipId"
        loading={loading}
        dataSource={rows}
        pagination={false}
        columns={[
          {
            title: '姓名',
            dataIndex: 'name',
            width: 220,
            render: (name: string, row) => (
              <Space size={4}>
                {/*
                  姓名只有管理员能设置；其他人看到的是纯文本，没有编辑入口。
                  和角色列一样用**常驻控件**而不是点一下才出现的行内编辑：表格列里
                  那种编辑框会被挤成一条缝，根本看不清自己在改什么。
                */}
                {isAdmin ? (
                  <Input
                    size="small"
                    style={{ width: 140 }}
                    maxLength={100}
                    aria-label={`${name} 的姓名`}
                    value={nameDraft[row.membershipId] ?? name}
                    onChange={(e) =>
                      setNameDraft((d) => ({ ...d, [row.membershipId]: e.target.value }))
                    }
                    onBlur={() => void commitName(row)}
                    onPressEnter={(e) => e.currentTarget.blur()}
                  />
                ) : (
                  name
                )}
                {row.userId === me?.user.id && <Tag style={{ marginInlineEnd: 0 }}>我</Tag>}
              </Space>
            ),
          },
          { title: '邮箱', dataIndex: 'email' },
          {
            title: '角色',
            dataIndex: 'roles',
            width: 280,
            render: (roles: Role[], row) =>
              // 非管理员只看标签；管理员直接在表格里改，不用再进弹窗
              isAdmin ? (
                <Tooltip
                  title={
                    isLastAdmin(row)
                      ? '组织里至少要保留一个管理员，不能摘掉他的管理员角色'
                      : undefined
                  }
                >
                  <Select<Role[]>
                    mode="multiple"
                    size="small"
                    style={{ width: '100%', minWidth: 220 }}
                    value={draft[row.membershipId] ?? row.roles}
                    options={ROLE_OPTIONS}
                    disabled={row.status !== 'active'}
                    onChange={(next) =>
                      setDraft((d) => ({ ...d, [row.membershipId]: next }))
                    }
                    onBlur={() => void commitRoles(row)}
                  />
                </Tooltip>
              ) : (
                <Space size={4} wrap>
                  {normalizeRoles(roles).map((r) => (
                    <Tag key={r} color={ROLE_COLOR[r]} style={{ marginInlineEnd: 0 }}>
                      {ROLE_LABEL[r]}
                    </Tag>
                  ))}
                </Space>
              ),
          },
          {
            title: '状态',
            dataIndex: 'status',
            width: 90,
            render: (s: Member['status']) =>
              s === 'active' ? <Tag color="green">正常</Tag> : <Tag>已移除</Tag>,
          },
          {
            title: '加入时间',
            dataIndex: 'createdAt',
            width: 160,
            // 后端返回 ISO 串（UTC），按浏览器本地时区展示
            render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
          },
          ...(isAdmin
            ? [
                {
                  title: '操作',
                  width: 100,
                  render: (_: unknown, row: Member) => {
                    const isSelf = row.userId === me?.user.id;
                    const blocked = isSelf || isLastAdmin(row);
                    const reason = isSelf
                      ? '不能移除自己'
                      : isLastAdmin(row)
                        ? '组织里至少要保留一个管理员'
                        : undefined;

                    if (row.status !== 'active') {
                      return (
                        <Button type="link" size="small" onClick={() => toggleStatus(row)}>
                          恢复
                        </Button>
                      );
                    }
                    return (
                      <Tooltip title={reason}>
                        <Popconfirm
                          title={`确认移除 ${row.name}？`}
                          description="移除后无法登录，之后可以恢复。"
                          disabled={blocked}
                          onConfirm={() => toggleStatus(row)}
                        >
                          <Button danger type="link" size="small" disabled={blocked}>
                            移除
                          </Button>
                        </Popconfirm>
                      </Tooltip>
                    );
                  },
                },
              ]
            : []),
        ]}
      />

      <Modal
        open={inviting}
        title="添加成员"
        okText="添加并发送邀请"
        confirmLoading={submitting}
        onCancel={() => setInviting(false)}
        onOk={submitInvite}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" initialValues={{ roles: ['member'] }}>
          <Form.Item
            name="name"
            label="姓名"
            rules={[{ required: true, message: '请输入姓名' }, { max: 100 }]}
          >
            <Input autoFocus />
          </Form.Item>
          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '邮箱格式不正确' },
            ]}
            extra="对方用这个邮箱收验证码登录，加入后会收到一封邀请邮件。"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="roles"
            label="角色"
            rules={[{ required: true, message: '至少选一个角色' }]}
            extra="可以同时选多个，权限取并集。"
          >
            <Select mode="multiple" options={ROLE_OPTIONS} placeholder="可多选" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
